import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MessageSquare, Inbox as InboxIcon, ArrowRight, Send } from "lucide-react";
import { http } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import DashShell from "@/components/dash/DashShell";
import { Card } from "@/components/portal/ui";
import NotificationBell from "@/components/dash/NotificationBell";
import { OP_LINKS } from "./OperatorDashboard";

const NAMES = { diagnostic: "Clarity", blueprint: "Blueprint", guided: "Guided Virtual Consulting", inhome_overnight: "In-Home + Overnight", general: "Messages" };

export default function OperatorInbox() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("unread");
  useSeo({ title: "Communication Center", noindex: true });
  const load = () => http.get("/operator/inbox").then((r) => setD(r.data)).catch(() => setD({ conversations: [], unread_total: 0 }));
  useEffect(() => { load(); }, []);

  if (!d) return <DashShell brandLabel="Consultant Workspace" links={OP_LINKS("inbox")} rightSlot={<NotificationBell />}><p className="text-[#6B665E]">Loading…</p></DashShell>;
  const convos = d.conversations.filter((c) => filter === "all" || c.unread > 0);

  return (
    <DashShell brandLabel="Consultant Workspace" links={OP_LINKS("inbox")} rightSlot={<NotificationBell />}>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="eyebrow text-[#8A998C] mb-1">Communication center</div>
          <h1 className="font-display text-3xl text-[#1A1918]">Inbox</h1>
        </div>
        <div className="flex gap-2">
          {[["unread", `Unread (${d.unread_total})`], ["all", "All conversations"]].map(([k, l]) => (
            <button key={k} data-testid={`inbox-filter-${k}`} onClick={() => setFilter(k)}
              className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${filter === k ? "bg-[#1A1918] text-white border-[#1A1918]" : "bg-white text-[#6B665E] border-[#E5E1D8] hover:border-[#9F634E]/40"}`}>{l}</button>
          ))}
        </div>
      </div>

      {convos.length === 0 ? (
        <Card className="p-10 text-center" data-testid="inbox-empty">
          <InboxIcon size={24} className="text-[#C9C3B8] mx-auto mb-3" />
          <h3 className="font-display text-lg text-[#1A1918] mb-1">{filter === "unread" ? "No unread messages" : "No conversations yet"}</h3>
          <p className="text-[#6B665E] text-sm">{filter === "unread" ? "You're all caught up — every client reply has been read." : "Client messages will appear here once a Guided client writes in."}</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="inbox-list">
          {convos.map((c) => (
            <ConversationRow key={`${c.customer_id}-${c.product_key}`} c={c} nav={nav} onReplied={load} />
          ))}
        </div>
      )}
    </DashShell>
  );
}

function ConversationRow({ c, nav, onReplied }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await http.post("/operator/message", { customer_id: c.customer_id, product: c.product_key, text });
      setText(""); setOpen(false);
      toast.success(`Reply sent to ${c.name}`);
      onReplied();
    } catch (e) { toast.error("Could not send reply"); } finally { setSending(false); }
  };

  return (
    <Card className={`p-0 overflow-hidden ${c.unread > 0 ? "border-[#9F634E]/30" : ""}`} data-testid={`inbox-row-${c.customer_id}`}>
      <div className="p-4 flex items-center justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="w-9 h-9 rounded-full bg-[#9F634E]/10 grid place-items-center shrink-0"><MessageSquare size={15} className="text-[#9F634E]" /></span>
          <div className="min-w-0">
            <div className="text-[#1A1918] text-sm font-medium flex items-center gap-2 flex-wrap">
              {c.name}
              {c.is_test && <span className="text-[9px] uppercase bg-[#C49A5A]/20 text-[#8a6a2e] rounded-full px-1.5 py-0.5">Test</span>}
              <span className="text-[#8A998C] text-xs font-normal">· {NAMES[c.product_key] || c.product_key}</span>
            </div>
            <div className="text-[#6B665E] text-sm truncate max-w-md">{c.last_sender === "customer" ? "" : "You: "}{c.last_text}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {c.unread > 0 && <span data-testid={`inbox-unread-${c.customer_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-[#9F634E] text-white rounded-full px-2 py-0.5"><Mail size={10} /> {c.unread}</span>}
          {c.waiting_on === "consultant" && c.unread === 0 && <span className="text-[10px] text-[#9F634E]">Needs reply</span>}
          <button data-testid={`inbox-reply-toggle-${c.customer_id}`} onClick={() => setOpen((v) => !v)}
            className="text-xs rounded-full px-3 py-1.5 border border-[#E5E1D8] text-[#6B665E] hover:border-[#9F634E]/40 transition-colors">{open ? "Cancel" : "Reply"}</button>
          <button data-testid={`inbox-open-${c.customer_id}`} onClick={() => nav(`/operator/customer/${c.customer_id}`)} className="text-[#8A998C] hover:text-[#1A1918]"><ArrowRight size={16} /></button>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-[#F0ECE4] bg-[#FAF8F3]">
          <textarea data-testid={`inbox-reply-text-${c.customer_id}`} rows={3} autoFocus value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") send(); }}
            placeholder={`Reply to ${c.name}…`}
            className="w-full mt-3 px-3 py-2.5 rounded-lg border border-[#E5E1D8] bg-white text-sm text-[#1A1918] focus:border-[#9F634E] outline-none" />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px] text-[#A39E98]">Sends to the client's portal · ⌘/Ctrl+Enter</span>
            <button data-testid={`inbox-reply-send-${c.customer_id}`} onClick={send} disabled={sending || !text.trim()}
              className="inline-flex items-center gap-1.5 bg-[#9F634E] text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-[#8a5342] transition-colors disabled:opacity-50"><Send size={13} /> {sending ? "Sending…" : "Send reply"}</button>
          </div>
        </div>
      )}
    </Card>
  );
}
